'use client'

import { useCallback, useEffect, useState } from 'react'


import { clientVerifyAnnualProgram } from '@/app/[locale]/order/_services/clientVerifyAnnualProgram'

import { CartProductType } from '../../../../_context/order-flow-context'

import type { PatientFormValues } from '../_types/patient-form.types'
import type { IAnnualProgramDiscountData } from '@/app/[locale]/order/_types/annual-program-discount'
import type { BirthNumberValidationResult } from '@/app/[locale]/order/_utils/validate-birth-number'
import type { FieldErrors } from 'react-hook-form'

type UseAnnualProgramDiscountParams = {
	primaryType: CartProductType | undefined
	firstName: string
	lastName: string
	birthNumber: string
	errors: Pick<FieldErrors<PatientFormValues>, 'firstName' | 'lastName' | 'birthNumber'>
	birthNumberValidation: BirthNumberValidationResult | null
	templateId: number | undefined
}

/**
 * Debounced client verification for annual-program discounts (template orders only).
 */
export function useAnnualProgramDiscount({
	primaryType,
	firstName,
	lastName,
	birthNumber,
	errors,
	birthNumberValidation,
	templateId
}: UseAnnualProgramDiscountParams) {
	const [annualProgramDiscount, setAnnualProgramDiscount] =
		useState<IAnnualProgramDiscountData | null>(null)
	const [isCheckingDiscount, setIsCheckingDiscount] = useState(false)

	const checkDiscount = useCallback(
		async (fn: string, ln: string, bn: string, tid: number | undefined) => {
			if (!tid) return
			setIsCheckingDiscount(true)
			try {
				const result = await clientVerifyAnnualProgram({
					firstName: fn,
					lastName: ln,
					birthNumber: bn,
					templateId: tid
				})
				setAnnualProgramDiscount(result)
			} catch {
				setAnnualProgramDiscount(null)
			} finally {
				setIsCheckingDiscount(false)
			}
		},
		[]
	)

	useEffect(() => {
		if (primaryType !== CartProductType.TEMPLATE) return
		if (!firstName || !lastName || !birthNumber) return
		if (errors.firstName || errors.lastName || errors.birthNumber) return
		if (!birthNumberValidation?.isValid) return

		const timeout = setTimeout(() => {
			checkDiscount(firstName, lastName, birthNumber, templateId)
		}, 800)
		return () => clearTimeout(timeout)
	}, [
		primaryType,
		firstName,
		lastName,
		birthNumber,
		errors.firstName,
		errors.lastName,
		errors.birthNumber,
		birthNumberValidation?.isValid,
		templateId,
		checkDiscount
	])

	return { annualProgramDiscount, isCheckingDiscount }
}
